import React from 'react';
import * as Yup from 'yup';
import { Button, Dialog, DialogProps, Group, Tabs } from '@economic/taco';
import { useCurrentAgreement } from '@economic/react-app';

import { Entity } from '~/types';
import { Dimension, DimensionsAccess } from '~/app/shared/dimensions/types';
import DimensionLineCombobox from '~/app/shared/dimensions/components/DimensionLineCombobox';
import useDimensionsAccessLevel from '~/app/shared/dimensions/hooks/useDimensionsAccessLevel';
import { Column, Row } from '../../shared/components/Grid/Grid';
import { Field, Form } from '../../shared/components/Form';
import { t, useLocalization } from '../../shared/components/l10n/l10n';
import internalApiClient from '../../shared/util/api/internalApiClient';
import { FormProps } from '../types';
import { ProductGroup, ProductRequest, ProductResponse as Product, UnitSetup } from '../../../types/internal-api';
import Loader from '../../shared/components/Loader';
import ProductGroupCombobox from '../../shared/components/ui/ProductGroupCombobox';
import LocationCombobox from '../../shared/components/ui/LocationCombobox';
import DepartmentCombobox from '../../shared/components/ui/DepartmentCombobox';
import AccessDropdown from '../../shared/components/ui/AccessDropdown';
import UnitDropdown from '../../shared/components/ui/UnitDropdown';
import useCachedQuery from '../../shared/hooks/useCachedQuery';
import ConfirmationDialog from '../../shared/components/modals/ConfirmationDialog';
import ProductCategoryCombobox from '../../shared/components/ui/ProductCategoryCombobox';
import ProductDiscountGroupCombobox from '../../shared/components/ui/ProductDiscountGroupCombobox';
import { useDisableKeyShortcuts } from '../../shared/hooks/useKeyShortcut';
import useQuery from '../../shared/hooks/useQuery';
import AmountInput from '../../shared/components/ui/AmountInput';

type ProductFormProps = Omit<FormProps<Product>, 'onSubmitted'> & {
    showSecondSalesPrice: boolean;
    baseCurrency?: string;
    showUnit2: boolean;
    showPackagingEnvironmentFeeRate: boolean;
    isNumberChangeable: boolean;
    editOnSubmit: boolean;
    showSaveAndNew?: boolean;
    onSubmitted?: (response: Product, createNew?: boolean) => void;
    arrowKeySaveAndChangeHandler?: (nextItemId: string | number) => void;
    collectionData?: any[];
    idKeyProp?: string;
    currentItemId?: string | number;
    dimensionDefinitions?: Dimension[];
};
type CreateOrEditProductProps = Omit<
    ProductFormProps,
    'showUnit2' | 'showPackagingEnvironmentFeeRate' | 'isNumberChangeable' | 'initialData' | 'editOnSubmit'
> & { productNumber?: string; copyProduct?: boolean };
type ProductDialogProps = Omit<DialogProps, 'children'> & CreateOrEditProductProps;
interface SubmitButtonProps {
    showConfirmationDialog: boolean;
    showSaveAndNew: boolean;
    setCreateNew: (createNew: boolean) => void;
    handleFormSubmit: (
        event?: Partial<Pick<React.SyntheticEvent, 'preventDefault' | 'stopPropagation'>>
    ) => Promise<Record<string, any> | undefined> | undefined;
}

enum CreateProductTab {
    General = 'tab-general',
    Inventory = 'tab-inventory',
}

const initialValues: Partial<Product> = {
    productNumber: undefined,
    productGroupId: undefined,
    name: undefined,
    description: undefined,
    barCode: undefined,
    salesPrice: undefined,
    costPrice: undefined,
    recommendedPrice: undefined,
    unitId: undefined,
    isAccessible: true,
    volume: undefined,
    departmentOrDistributionKeyId: undefined,
    recommendedCostPrice: undefined,
    grossWeight: undefined,
    netWeight: undefined,
    minimumStock: undefined,
    minimumOrder: undefined,
    defaultLocationId: undefined,
    serialNumberPolicy: undefined,
    unit: undefined,
    unit2Id: undefined,
    environmentalFeeRate: undefined,
    packagingRate: undefined,
    categoryNumber: undefined,
    minimumInventory: undefined,
    maximumInventory: undefined,
    productDiscountGroupNumber: undefined,
    secondSalesPrice: undefined,
    dimensions: [],
};

const isProductNumberInUse = async (value: string): Promise<boolean> => {
    if (!value) {
        return false;
    }

    // Exception handling as normal control flow... not ideal.
    // the intent here is to treat a server error in fetching a product by its product number as an existence check.
    try {
        await internalApiClient.get<Product>(`/products?productNumber=${value}`);
    } catch {
        // we failed to get the product, so we assume the product number doesn't exist...
        // this is potentially fragile (product might exist, but something else happened).
        return false;
    }

    return true;
};

const validationSchema = (isNumberChangeable: boolean, isEdit: boolean, originalProductNumber?: string) =>
    Yup.object().shape({
        productNumber: Yup.string()
            .required()
            .test({
                name: 'productNumber',
                async test(value) {
                    if (isEdit && originalProductNumber && value !== originalProductNumber) {
                        if (!isNumberChangeable) {
                            return this.createError({
                                message: t`ERROR_CODE_ProductNumberChangeSettingNotEnabled`,
                            });
                        }
                        const isInUse = await isProductNumberInUse(value);
                        if (isInUse) {
                            return this.createError({
                                message: t`FORM_GOODS_ERROR_GOODSNOEXISTS`,
                            });
                        }
                        if (value && value.trim() === '') {
                            return this.createError({
                                message: t`FORM_FIELD_LABEL_REQUIRED`,
                            });
                        }
                    }
                    return true;
                },
            }),
        name: Yup.string().max(300).required(),
        productGroupId: Yup.number().required(),
        departmentOrDistributionKeyId: Yup.number().nullable(),
        description: Yup.string().nullable(),
        salesPrice: Yup.number().nullable(),
        recommendedPrice: Yup.number().nullable(),
        costPrice: Yup.number().nullable(),
        recommendedCostPrice: Yup.number()
            .nullable()
            .when('$agreement', {
                is: agreement => agreement?.modules?.inventory === true,
                then: Yup.number().min(0).required(),
            }),
        barCode: Yup.string().nullable(),
        isAccessible: Yup.bool().nullable(),
        unitId: Yup.number().nullable(),
        unit2Id: Yup.number().nullable(),
        categoryNumber: Yup.number().nullable(),
        minimumStock: Yup.number().nullable(),
        minimumOrder: Yup.number().nullable(),
        defaultLocationId: Yup.number().nullable(),
        productDiscountGroupNumber: Yup.number().nullable(),
        grossWeight: Yup.number().nullable(),
        netWeight: Yup.number().nullable(),
        volume: Yup.number().nullable(),
        packagingRate: Yup.number().nullable(),
        environmentalFeeRate: Yup.number().nullable(),
        secondSalesPrice: Yup.number().nullable(),
    });

function SubmitButton({ showConfirmationDialog, handleFormSubmit, showSaveAndNew, setCreateNew }: SubmitButtonProps) {
    let submitDialog;

    if (showSaveAndNew) {
        return (
            <>
                <Button
                    type="submit"
                    onClick={e => {
                        // Makes sure that when user press enter, all the fields are blurred
                        // and form values are updated before submit
                        (e.target as any).focus();

                        setCreateNew(true);
                    }}
                    appearance="primary">
                    {t`BUTTON_SAVE_AND_NEW`}
                </Button>
                <Button
                    type="button"
                    appearance="default"
                    onClick={e => {
                        setCreateNew(false);
                        handleFormSubmit(e);
                    }}>
                    {t`BUTTON_SAVE`}
                </Button>
            </>
        );
    }

    if (showConfirmationDialog) {
        submitDialog = props => (
            <Dialog size="xs" {...props}>
                <Dialog.Content aria-label={t`DIALOG_CHANGE_NUMBER`}>
                    {({ close }) => (
                        <ConfirmationDialog
                            label={t`FORM_GOODS_CHANGENO_HEADLINE`}
                            message={t`FORM_GOODS_CHANGENO_CONFIRM`}
                            onCancel={close}
                            onConfirm={handleFormSubmit}
                        />
                    )}
                </Dialog.Content>
            </Dialog>
        );
    }

    return (
        <Button
            type={showConfirmationDialog ? 'button' : 'submit'}
            onClick={e => {
                // Makes sure that when user press enter, all the fields are blurred
                // and form values are updated before submit
                (e.target as any).focus();
            }}
            dialog={submitDialog}
            appearance="primary">{t`BUTTON_SAVE`}</Button>
    );
}

const DimensionField = ({ index, dimensionDefinition }: { index: number; dimensionDefinition: Dimension }) => (
    <Group className="w-full flex flex-row justify-between mb-2">
        <Field.Hidden name={`dimensions[${index}].dimensionId`} initialValue={dimensionDefinition.id} />
        <Field.Combobox
            name={`dimensions[${index}].dimensionKey`}
            dimensionId={dimensionDefinition.id}
            fieldClassName="flex flex-1 flex-col -mb-4"
            label={dimensionDefinition.name}
            as={DimensionLineCombobox}
            disabled={false}
        />
    </Group>
);

function ProductForm({
    initialData,
    notifications,
    onCancel,
    onSubmitted: handleSubmitted,
    showSecondSalesPrice,
    baseCurrency,
    showPackagingEnvironmentFeeRate,
    showUnit2,
    isNumberChangeable,
    editOnSubmit,
    showSaveAndNew,
    arrowKeySaveAndChangeHandler,
    collectionData,
    idKeyProp,
    currentItemId,
    dimensionDefinitions,
}: ProductFormProps) {
    const [createNew, setCreateNew] = React.useState(false);
    const agreement = useCurrentAgreement();
    const productGroupsQuery = useCachedQuery<{ data: ProductGroup[] }>('/products/groups', []);

    const hasDimension = useDimensionsAccessLevel(DimensionsAccess.HasDimension);
    const hasMultiDimensions = useDimensionsAccessLevel(DimensionsAccess.HasMultiDimensions);
    const hasInventoryModule = !!agreement.modules.inventory;

    const oldProductNumber = initialData?.productNumber;
    const productGroups = productGroupsQuery?.data.data;

    const shouldShowConfirmationDialog = (newProductNumber: string) =>
        isNumberChangeable && oldProductNumber !== undefined && newProductNumber !== oldProductNumber && editOnSubmit;
    const isProductGroupUsingInventory = (productGroupId: Product['productGroupId']) =>
        productGroups?.find((pg: ProductGroup) => pg.id === Number(productGroupId))?.isServiceProducts;

    const handleSubmit = async (values: Product) => {
        try {
            let response;
            let secondSalesPrice;

            if (showSecondSalesPrice && values.secondSalesPrice !== undefined) {
                secondSalesPrice = {
                    currency: baseCurrency,
                    salesPrice: values.secondSalesPrice,
                };
            }

            // Removing isVariantInUse, which is a product response only value.
            const { isVariantInUse: _isVariantInUse, ...partialProductRequestValues } = values;
            const valuesWithSecondSalesPrice: ProductRequest = { ...partialProductRequestValues, secondSalesPrice };
            // EC-33998: minimun stock has to default to 0 instead of null
            if (
                valuesWithSecondSalesPrice.hasOwnProperty('minimumStock') &&
                (valuesWithSecondSalesPrice.minimumStock === undefined || valuesWithSecondSalesPrice.minimumStock === null)
            ) {
                valuesWithSecondSalesPrice.minimumStock = 0;
            }
            if (editOnSubmit) {
                const encodedProductNumber = initialData?.productNumber && encodeURIComponent(initialData.productNumber);
                response = await internalApiClient.put(
                    `/products?productNumber=${encodedProductNumber}`,
                    valuesWithSecondSalesPrice
                );
            } else {
                response = await internalApiClient.post(`/products`, valuesWithSecondSalesPrice);
            }

            return Promise.resolve(response);
        } catch (error) {
            return Promise.reject(error);
        }
    };

    return (
        <Form
            context={{ agreement }}
            initialData={initialData}
            messages={notifications}
            onSubmit={handleSubmit}
            onSubmitted={product => handleSubmitted && handleSubmitted(product, createNew)}
            validationSchema={() => validationSchema(isNumberChangeable, editOnSubmit, initialData?.productNumber)}
            onArrowKeySaveAndChange={arrowKeySaveAndChangeHandler}
            collectionData={collectionData}
            idKeyProp={idKeyProp}
            currentItemId={currentItemId}>
            {({ values, handleSubmit: handleFormSubmit }) => (
                <>
                    <h1>{t`FORM_GOODS_APPLETHEADLINE`}</h1>
                    <Tabs aria-label={t`PRODUCT_DIALOG_TABS`} defaultId={CreateProductTab.General}>
                        <Tabs.List>
                            <Tabs.Trigger id={CreateProductTab.General}>{t`FIREBIRD_FORM_CUSTOMERS_TAB_GENERAL`}</Tabs.Trigger>
                            {hasInventoryModule && (
                                <Tabs.Trigger id={CreateProductTab.Inventory}>{t`FIREBIRD_TAB_INVENTORY`}</Tabs.Trigger>
                            )}
                        </Tabs.List>
                        <Tabs.Content id={CreateProductTab.General}>
                            <Row>
                                <Column>
                                    <Row>
                                        <Column>
                                            <Field.Text
                                                autoFocus
                                                label={t`FORM_FIELD_LABEL_NUMBER`}
                                                name="productNumber"
                                                required
                                            />
                                        </Column>
                                        <Column>
                                            <Field.Combobox
                                                as={ProductGroupCombobox}
                                                label={t`FORM_GOODS_EDITFIELD_GOODSGROUP`}
                                                name="productGroupId"
                                                required
                                            />
                                        </Column>
                                    </Row>
                                    <Row>
                                        <Column>
                                            <Field.Text label={t`FORM_FIELD_LABEL_NAME`} name="name" maxLength="300" required />
                                        </Column>
                                    </Row>

                                    {hasDimension && !hasMultiDimensions && (
                                        <Row>
                                            <Column>
                                                <Field.Combobox
                                                    as={DepartmentCombobox}
                                                    label={t`FORM_GOODS_EDITFIELD_DEPARTMENT`}
                                                    name="departmentOrDistributionKeyId"
                                                />
                                            </Column>
                                        </Row>
                                    )}

                                    {hasMultiDimensions &&
                                        (dimensionDefinitions?.length ?? 0) > 0 &&
                                        dimensionDefinitions![0].active && (
                                            <Row>
                                                <Column>
                                                    <DimensionField index={0} dimensionDefinition={dimensionDefinitions![0]} />
                                                </Column>
                                            </Row>
                                        )}
                                    {hasMultiDimensions &&
                                        (dimensionDefinitions?.length ?? 0) > 1 &&
                                        dimensionDefinitions![1].active && (
                                            <Row>
                                                <Column>
                                                    <DimensionField index={1} dimensionDefinition={dimensionDefinitions![1]} />
                                                </Column>
                                                {dimensionDefinitions!.length > 2 && dimensionDefinitions![2].active && (
                                                    <Column>
                                                        <DimensionField
                                                            index={2}
                                                            dimensionDefinition={dimensionDefinitions![2]}
                                                        />
                                                    </Column>
                                                )}
                                            </Row>
                                        )}
                                </Column>
                                <Column>
                                    <Field.TextArea
                                        className="resize-none"
                                        label={t`FORM_GOODS_EDITFIELD_DESCRIPTION`}
                                        name="description"
                                    />
                                    {hasDimension && showPackagingEnvironmentFeeRate && (
                                        <Row>
                                            <Column>
                                                <Field.Number decimal label={t`FORM_GOODS_EDITFIELD_RATE`} name="packagingRate" />
                                            </Column>
                                            <Column>
                                                <Field.Number
                                                    decimal
                                                    label={t`FORM_GOODS_EDITFIELD_ENVFEE`}
                                                    name="environmentalFeeRate"
                                                />
                                            </Column>
                                        </Row>
                                    )}
                                </Column>
                                <Column>
                                    <Field.Number
                                        decimal
                                        label={t`FORM_GOODS_EDITFIELD_SALESPRICE`}
                                        name="salesPrice"
                                        as={AmountInput}
                                    />
                                    <Row>
                                        {showSecondSalesPrice && (
                                            <Column>
                                                <Field.Number
                                                    decimal
                                                    label={`${t`FORM_GOODS_EDITFIELD_SALESPRICE`} ${baseCurrency}`}
                                                    name="secondSalesPrice"
                                                    as={AmountInput}
                                                />
                                            </Column>
                                        )}
                                        <Column>
                                            <Field.Number
                                                decimal
                                                label={t`FORM_GOODS_EDITFIELD_SUGGPRICE`}
                                                name="recommendedPrice"
                                                as={AmountInput}
                                            />
                                        </Column>
                                    </Row>
                                    <Field.Number
                                        decimal
                                        label={t`FORM_GOODS_EDITFIELD_COSTPRICE`}
                                        name="costPrice"
                                        as={AmountInput}
                                        disabled={hasInventoryModule && !isProductGroupUsingInventory(values.productGroupId)}
                                    />
                                    {hasInventoryModule && (
                                        <Field.Number
                                            decimal
                                            label={t`FORM_GOODS_EDITFIELD_SUGGESTIVECOSTPRICE`}
                                            name="recommendedCostPrice"
                                            required
                                            as={AmountInput}
                                        />
                                    )}
                                </Column>
                                <Column>
                                    <Field.Text label={t`FORM_GOODS_EDITFIELD_BARCODE`} name="barCode" />
                                    <Field.Dropdown
                                        as={AccessDropdown}
                                        label={t`FORM_GOODS_EDITFIELD_ACCESS`}
                                        name="isAccessible"
                                    />
                                    <Row>
                                        <Column>
                                            <Field.Dropdown
                                                as={UnitDropdown}
                                                label={t`FORM_GOODS_EDITFIELD_UNIT`}
                                                name="unitId"
                                                emptyValue={null}
                                            />
                                        </Column>
                                        {hasDimension && showUnit2 && (
                                            <Column>
                                                <Field.Dropdown
                                                    as={UnitDropdown}
                                                    label={t`FORM_GOODS_EDITFIELD_UNIT2`}
                                                    name="unit2Id"
                                                    emptyValue={null}
                                                />
                                            </Column>
                                        )}
                                    </Row>
                                </Column>
                            </Row>
                        </Tabs.Content>
                        {hasInventoryModule && (
                            <Tabs.Content id={CreateProductTab.Inventory} title={t`FIREBIRD_TAB_INVENTORY`}>
                                <Row>
                                    <Column xs="3">
                                        <Field.Combobox
                                            as={ProductCategoryCombobox}
                                            label={t`FORM_GOODS_EDITFIELD_CATEGORY`}
                                            name="categoryNumber"
                                            readOnly={values.isVariantInUse}
                                        />
                                        <Row>
                                            <Column>
                                                <Field.Number
                                                    decimal
                                                    label={t`FORM_GOODS_EDITFIELD_MINIMUMSTOCK`}
                                                    name="minimumStock"
                                                    as={AmountInput}
                                                />
                                            </Column>
                                            <Column>
                                                <Field.Number
                                                    decimal
                                                    label={t`FORM_GOODS_EDITFIELD_MINIMUMORDER`}
                                                    name="minimumOrder"
                                                    as={AmountInput}
                                                />
                                            </Column>
                                        </Row>
                                        <Field.Combobox
                                            as={LocationCombobox}
                                            label={t`FORM_GOODS_EDITFIELD_LOCATION`}
                                            name="defaultLocationId"
                                        />
                                    </Column>
                                    <Column xs="3">
                                        <Field.Number
                                            decimal
                                            label={t`FORM_GOODS_EDITFIELD_GROSSWEIGHT`}
                                            name="grossWeight"
                                            as={AmountInput}
                                        />
                                        <Field.Number
                                            decimal
                                            label={t`FORM_GOODS_EDITFIELD_NETWEIGHT`}
                                            name="netWeight"
                                            as={AmountInput}
                                        />
                                        <Field.Number
                                            decimal
                                            label={t`FORM_GOODS_EDITFIELD_VOLUME`}
                                            name="volume"
                                            as={AmountInput}
                                        />
                                    </Column>
                                    <Column xs="3">
                                        <Field.Combobox
                                            as={ProductDiscountGroupCombobox}
                                            label={t`FORM_GOODS_EDITFIELD_GOODSDISCOUNTGROUP`}
                                            name="productDiscountGroupNumber"
                                        />
                                    </Column>
                                </Row>
                            </Tabs.Content>
                        )}
                    </Tabs>
                    <div className="form__buttons">
                        <SubmitButton
                            showConfirmationDialog={shouldShowConfirmationDialog(values.productNumber)}
                            handleFormSubmit={handleFormSubmit}
                            showSaveAndNew={!!showSaveAndNew}
                            setCreateNew={setCreateNew}
                        />
                        {onCancel && (
                            <Button onClick={onCancel} appearance="default">
                                {t`BUTTON_CANCEL`}
                            </Button>
                        )}
                    </div>
                </>
            )}
        </Form>
    );
}

export function CreateOrEditProduct({
    productNumber,
    copyProduct: shouldCopyProductOnSubmit = false,
    onSubmitted: handleSubmitted,
    collectionData,
    idKeyProp,
    currentItemId,
    ...props
}: CreateOrEditProductProps) {
    const shouldCreateProductOnSubmit = productNumber === undefined;
    const shouldEditOnSubmit = !shouldCreateProductOnSubmit && !shouldCopyProductOnSubmit;
    const encodedProductNumber = productNumber && encodeURIComponent(productNumber);

    const hasMultiDimensions = useDimensionsAccessLevel(DimensionsAccess.HasMultiDimensions);
    const dimensionDefinitionsQuery = useQuery<Dimension[]>(
        `/dimension/GetAll?includeDistributionKeys=${true}&active=${true}`,
        undefined,
        {
            skip: !hasMultiDimensions,
        }
    );

    const dimensionSettingsQuery = useCachedQuery<UnitSetup>('/setup/units-setup', {});
    const changeProductNumberQuery = useCachedQuery<boolean>(`/settings/entity-numbers/${Entity.Product}`, false);
    const nextProductNumberQuery = useQuery<number>('/products/next-product-number');
    const productAutoNumberQuery = useCachedQuery<boolean>('/products/settings/uses-product-auto-numbering', false);
    const productQuery = useQuery<Product>(
        `/products?productNumber=${encodedProductNumber}&currency=${props.baseCurrency}`,
        {},
        { skip: shouldCreateProductOnSubmit }
    );
    const loadingl10n = useLocalization(textIds);

    useDisableKeyShortcuts();

    const loading =
        loadingl10n ||
        changeProductNumberQuery.loading ||
        dimensionSettingsQuery.loading ||
        nextProductNumberQuery.loading ||
        productAutoNumberQuery.loading ||
        dimensionDefinitionsQuery.loading ||
        (productQuery.loading && !shouldCreateProductOnSubmit);

    if (loading) {
        return (
            <div className="h-40">
                <Loader />
            </div>
        );
    }

    const shouldShowNextProductNumber = productAutoNumberQuery.data;
    const nextProductNumber = nextProductNumberQuery.data;
    let initialData: Partial<Product>;
    if (shouldCreateProductOnSubmit) {
        initialData = initialValues;

        // nextProductNumber is a number that is not -1
        if (shouldShowNextProductNumber && nextProductNumber !== -1) {
            initialData = {
                ...initialData,
                productNumber: nextProductNumber.toString(),
            };
        }
    } else {
        // edit case
        initialData = {
            ...productQuery.data,
            costPrice:
                productQuery.data.costPrice && productQuery.data.costPrice < 0 && shouldCopyProductOnSubmit
                    ? 0
                    : productQuery.data.costPrice,
            dimensions: dimensionDefinitionsQuery?.data
                ?.filter(dimensionDefinition => dimensionDefinition.active)
                .map(dimensionDefinition => {
                    const dimensionValue = productQuery.data?.dimensions?.find(
                        dimensionValue => dimensionValue.dimensionId === dimensionDefinition.id
                    );

                    return (
                        dimensionValue ?? {
                            dimensionId: dimensionDefinition.id,
                        }
                    );
                }),
        };
    }

    return (
        <ProductForm
            showUnit2={dimensionSettingsQuery.data.showUnit2}
            showPackagingEnvironmentFeeRate={dimensionSettingsQuery.data.showTaxes}
            isNumberChangeable={changeProductNumberQuery.data}
            initialData={initialData}
            onSubmitted={(product, createNew) => {
                handleSubmitted?.(product, createNew);
            }}
            editOnSubmit={shouldEditOnSubmit}
            collectionData={collectionData}
            idKeyProp={idKeyProp}
            currentItemId={currentItemId}
            dimensionDefinitions={dimensionDefinitionsQuery.data}
            {...props}
        />
    );
}

export const EditProductDialog = ({
    onSubmitted,
    showSecondSalesPrice,
    baseCurrency,
    productNumber,
    copyProduct,
    arrowKeySaveAndChangeHandler,
    collectionData,
    idKeyProp,
    currentItemId,
    ...dialogProps
}: ProductDialogProps) => {
    const handleClose = (close: () => void) => {
        close();
        if (dialogProps.onClose) {
            dialogProps.onClose();
        }
    };

    return (
        <Dialog {...dialogProps} size="lg" draggable>
            <Dialog.Content aria-label={t`FORM_GOODS_CREATETEXT`}>
                {({ close }) => (
                    <CreateOrEditProduct
                        onCancel={() => handleClose(close)}
                        onSubmitted={product => {
                            onSubmitted?.(product);
                            handleClose(close);
                        }}
                        showSecondSalesPrice={showSecondSalesPrice}
                        baseCurrency={baseCurrency}
                        productNumber={productNumber}
                        copyProduct={copyProduct}
                        arrowKeySaveAndChangeHandler={arrowKeySaveAndChangeHandler}
                        collectionData={collectionData}
                        currentItemId={currentItemId}
                        idKeyProp={idKeyProp}
                    />
                )}
            </Dialog.Content>
        </Dialog>
    );
};

export const CreateProductDialog = ({
    onSubmitted,
    showSecondSalesPrice,
    baseCurrency,
    productNumber,
    copyProduct,
    showSaveAndNew = true,
    ...dialogProps
}: ProductDialogProps) => {
    // Count number of creations to allow mounting new component if user selects Save and New
    const [createCounter, setCreateCounter] = React.useState(1);

    const handleClose = (close: () => void) => {
        close();
        if (dialogProps.onClose) {
            dialogProps.onClose();
        }
    };

    return (
        <Dialog {...dialogProps} size="lg" draggable>
            <Dialog.Content aria-label={t`FORM_GOODS_CREATETEXT`}>
                {({ close }) => (
                    <CreateOrEditProduct
                        onCancel={() => handleClose(close)}
                        onSubmitted={(product, createNew) => {
                            onSubmitted?.(product, createNew);
                            if (!createNew) {
                                handleClose(close);
                            } else {
                                setCreateCounter(createCounter + 1);
                            }
                        }}
                        showSecondSalesPrice={showSecondSalesPrice}
                        baseCurrency={baseCurrency}
                        productNumber={productNumber}
                        copyProduct={copyProduct}
                        showSaveAndNew={showSaveAndNew}
                        key={createCounter}
                    />
                )}
            </Dialog.Content>
        </Dialog>
    );
};

const textIds = [
    'FIREBIRD_FORM_CUSTOMERS_TAB_GENERAL',
    'FIREBIRD_TAB_INVENTORY',
    'FORM_GOODS_CREATETEXT',
    'FORM_GOODS_APPLETHEADLINE',
    'PRODUCT_DIALOG_TABS',
    'FORM_GOODS_EDITFIELD_GOODSGROUP',
    'FORM_GOODS_EDITFIELD_DEPARTMENT',
    'FORM_GOODS_EDITFIELD_DESCRIPTION',
    'FORM_GOODS_EDITFIELD_SALESPRICE',
    'FORM_GOODS_EDITFIELD_SUGGPRICE',
    'FORM_GOODS_EDITFIELD_COSTPRICE',
    'FORM_GOODS_EDITFIELD_LOCATION',
    'FORM_GOODS_EDITFIELD_SUGGESTIVECOSTPRICE',
    'FORM_GOODS_EDITFIELD_BARCODE',
    'FORM_GOODS_EDITFIELD_ACCESS',
    'FORM_GOODS_EDITFIELD_UNIT',
    'FORM_GOODS_EDITFIELD_UNIT2',
    'FORM_GOODS_EDITFIELD_CATEGORY',
    'FORM_GOODS_EDITFIELD_MINIMUMSTOCK',
    'FORM_GOODS_EDITFIELD_MINIMUMORDER',
    'FORM_GOODS_EDITFIELD_GROSSWEIGHT',
    'FORM_GOODS_EDITFIELD_NETWEIGHT',
    'FORM_GOODS_EDITFIELD_VOLUME',
    'FORM_GOODS_EDITFIELD_GOODSDISCOUNTGROUP',
    'FORM_GOODS_EDITFIELD_ENVFEE',
    'FORM_GOODS_EDITFIELD_RATE',
    'FORM_GOODS_CHANGENO_HEADLINE',
    'FORM_GOODS_CHANGENO_CONFIRM',
    'FORM_GOODS_ERROR_GOODSNOEXISTS',
    'FORM_FIELD_LABEL_REQUIRED',
    'DIALOG_CHANGE_NUMBER',
    'ERROR_CODE_OnlyOneItemPerDimension',
    'ERROR_CODE_OnlyOneDistributionAllowed',
];
